import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Circle, Compass, Calendar, DollarSign, Home, FileText, Briefcase, Shield, Heart, ChevronDown, ChevronUp, AlertCircle, MapPin, ExternalLink, Phone, Globe } from '../components/CustomIcons';
import ReentryTimeline from '../components/ReentryTimeline';

const REENTRY_RESOURCES = [
  // Employment
  { id: 'ceo', category: 'employment', name: 'Center for Employment Opportunities (CEO)', desc: 'Provides immediate, transitional employment to people recently released from incarceration, plus permanent job placement support.', phone: '1-888-433-7236', website: 'https://ceoworks.org', national: true },
  { id: 'honest-jobs', category: 'employment', name: 'Honest Jobs', desc: 'The largest U.S. job board dedicated exclusively to second-chance employment — 1,000+ fair-chance employers actively recruiting.', phone: null, website: 'https://honestjobs.co', national: true },
  { id: 'defy', category: 'employment', name: 'Defy Ventures', desc: 'Transforms formerly incarcerated people into entrepreneurs and professionals through business training and mentorship.', phone: null, website: 'https://defyventures.org', national: true },
  { id: 'homeboy', category: 'employment', name: 'Homeboy Industries', desc: 'The world\'s largest gang intervention, rehabilitation, and reentry program. Employment, tattoo removal, therapy, and education.', phone: '323-526-1254', website: 'https://homeboyindustries.org', national: false },
  { id: 'america-works', category: 'employment', name: 'America Works', desc: 'Employment placement agency specializing in helping people with barriers to employment, including those with criminal records.', phone: null, website: 'https://americaworks.com', national: true },
  // Housing
  { id: 'voa', category: 'housing', name: 'Volunteers of America', desc: 'Operates hundreds of transitional housing and reentry programs across the country for people leaving incarceration.', phone: '1-800-899-0089', website: 'https://volunteersofamerica.org', national: true },
  { id: 'salvation-army', category: 'housing', name: 'The Salvation Army', desc: 'Provides emergency and transitional housing, meals, and reentry support services nationwide.', phone: '1-800-725-2769', website: 'https://salvationarmyusa.org', national: true },
  { id: 'catholic-charities', category: 'housing', name: 'Catholic Charities USA', desc: 'Reentry housing assistance, case management, and social services regardless of faith. Largest nonprofit network in the U.S.', phone: '703-549-1390', website: 'https://catholiccharitiesusa.org', national: true },
  { id: 'urban-league', category: 'housing', name: 'National Urban League', desc: 'Provides housing counseling, rental assistance, and economic empowerment programs for returning citizens.', phone: '212-558-5300', website: 'https://nul.org', national: true },
  // Legal Aid
  { id: 'lsc', category: 'legal', name: 'Legal Services Corporation', desc: 'Provides free civil legal aid to low-income Americans including expungement, housing, and benefits disputes.', phone: '202-295-1500', website: 'https://lsc.gov', national: true },
  { id: 'nlada', category: 'legal', name: 'National Legal Aid & Defender Assoc.', desc: 'Find free legal representation for civil and criminal matters. Includes expungement, record sealing, and reentry legal help.', phone: '202-452-0620', website: 'https://nlada.org', national: true },
  { id: 'nrrc', category: 'legal', name: 'National Reentry Resource Center', desc: 'Federal clearinghouse for reentry information — connects to state-specific resources, grants, and program directories.', phone: null, website: 'https://nationalreentryresourcecenter.org', national: true },
  { id: 'sentencing-project', category: 'legal', name: 'The Sentencing Project', desc: 'Advocacy and research on criminal justice reform. Publishes state-by-state guides to collateral consequences and restoration of rights.', phone: '202-628-0871', website: 'https://sentencingproject.org', national: true },
  // Mental Health
  { id: 'samhsa', category: 'mental_health', name: 'SAMHSA National Helpline', desc: 'Free, confidential, 24/7 treatment referral and information for mental health and substance use disorders. No insurance needed.', phone: '1-800-662-4357', website: 'https://samhsa.gov', national: true },
  { id: 'nami', category: 'mental_health', name: 'NAMI (Nat\'l Alliance on Mental Illness)', desc: 'Mental health education, peer support groups, and crisis resources. Find local chapters and telehealth options.', phone: '1-800-950-6264', website: 'https://nami.org', national: true },
  { id: 'crisis-text', category: 'mental_health', name: 'Crisis Text Line', desc: 'Free, 24/7 confidential crisis counseling via text message. No phone call required.', phone: 'Text HOME to 741741', website: 'https://crisistextline.org', national: true },
  // Financial
  { id: 'op-hope', category: 'financial', name: 'Operation HOPE', desc: 'Free financial coaching, credit-building, and banking access for underserved communities, including returning citizens.', phone: '1-888-388-4673', website: 'https://operationhope.org', national: true },
  { id: 'vita', category: 'financial', name: 'IRS VITA (Free Tax Prep)', desc: 'Free tax preparation for individuals earning under $67,000/year. VITA sites also help connect to earned income tax credit (EITC).', phone: '1-800-906-9887', website: 'https://irs.gov/vita', national: true },
  { id: 'bank-on', category: 'financial', name: 'Bank On (CFE Fund)', desc: 'Connects returning citizens to certified bank accounts with no overdraft fees and low minimum balances. Find local partner banks.', phone: null, website: 'https://cfefund.org/bank-on', national: true },
];

const RESOURCE_CATEGORIES = {
  employment: { label: 'Employment', icon: Briefcase, color: '#F97316' },
  housing: { label: 'Housing', icon: Home, color: '#3B82F6' },
  legal: { label: 'Legal Aid', icon: FileText, color: '#8B5CF6' },
  mental_health: { label: 'Mental Health', icon: Heart, color: '#EC4899' },
  financial: { label: 'Financial', icon: DollarSign, color: '#22C55E' },
};

const DEFAULT_ITEMS = [
  { id: 'id-state', category: 'documents', title: 'State-Issued ID or Driver\'s License', desc: 'Request at DMV with release paperwork. Some states issue an ID 30 days before release.', priority: 'high', completed: false },
  { id: 'id-social', category: 'documents', title: 'Social Security Card', desc: 'Apply at SSA office with ID and proof of citizenship. Free, takes 10–14 days.', priority: 'high', completed: false },
  { id: 'id-birth', category: 'documents', title: 'Birth Certificate', desc: 'Order from your state\'s vital records office. Typically $10–$25.', priority: 'high', completed: false },
  { id: 'housing-search', category: 'housing', title: 'Identify Housing Option', desc: 'Halfway house, transitional housing, family, or rental. Apply 60+ days before release.', priority: 'high', completed: false },
  { id: 'housing-snap', category: 'housing', title: 'Apply for Housing Voucher (Section 8)', desc: 'HUD Housing Choice Voucher waitlists vary by location. Start early.', priority: 'medium', completed: false },
  { id: 'benefits-snap', category: 'benefits', title: 'SNAP Food Benefits', desc: 'Most formerly incarcerated people qualify. Apply at local SNAP office on or after release.', priority: 'high', completed: false },
  { id: 'benefits-medicaid', category: 'benefits', title: 'Medicaid / Healthcare', desc: 'Re-enroll in Medicaid within 30 days of release if income-eligible.', priority: 'high', completed: false },
  { id: 'employment-resume', category: 'employment', title: 'Build Your Resume', desc: 'Use Assistiv OS Credentials to auto-generate a resume with your certifications.', priority: 'high', completed: false },
  { id: 'employment-apply', category: 'employment', title: 'Apply to Second-Chance Employers', desc: 'Our Jobs module has 40+ pre-screened employers. Apply before your release date.', priority: 'medium', completed: false },
  { id: 'transport-plan', category: 'transportation', title: 'Transportation Plan', desc: 'Public transit pass, rideshare credit, or identify a vehicle option.', priority: 'medium', completed: false },
  { id: 'bank-account', category: 'financial', title: 'Open a Bank Account', desc: 'Many banks offer second-chance accounts. Look for "Bank On" certified accounts in your area.', priority: 'high', completed: false },
  { id: 'support-family', category: 'support', title: 'Rebuild Family Connections', desc: 'Use Connect module to initiate contact. Family support is a key reentry protective factor.', priority: 'medium', completed: false },
  { id: 'support-mentor', category: 'support', title: 'Find a Mentor or Reentry Coach', desc: 'Ask your case manager to connect you with a peer mentor who has been through reentry.', priority: 'medium', completed: false },
  { id: 'health-mental', category: 'health', title: 'Mental Health Continuity', desc: 'If receiving treatment, establish care with a community provider before release.', priority: 'high', completed: false },
  { id: 'legal-probation', category: 'legal', title: 'Understand Parole/Probation Conditions', desc: 'Know your check-in schedule, travel restrictions, and employment requirements.', priority: 'high', completed: false },
];

const CATEGORY_LABELS = {
  documents: { label: 'Identity Documents', icon: FileText, color: '#F97316' },
  housing: { label: 'Housing', icon: Home, color: '#F97316' },
  benefits: { label: 'Benefits & Healthcare', icon: Heart, color: '#F97316' },
  employment: { label: 'Employment', icon: Briefcase, color: '#F97316' },
  transportation: { label: 'Transportation', icon: Compass, color: '#F97316' },
  financial: { label: 'Financial', icon: DollarSign, color: '#F97316' },
  support: { label: 'Support Network', icon: Shield, color: '#F97316' },
  health: { label: 'Health', icon: Heart, color: '#F97316' },
  legal: { label: 'Legal & Supervision', icon: FileText, color: '#F97316' },
};

export default function Reentry() {
  const { authFetch, user } = useAuth();
  const [items, setItems] = useState(DEFAULT_ITEMS);
  const [releaseInfo, setReleaseInfo] = useState(null);
  const [expandedCats, setExpandedCats] = useState({ documents: true, housing: true, benefits: true, employment: true });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('checklist');
  const [resourceCat, setResourceCat] = useState('all');

  useEffect(() => {
    authFetch('/reentry/release-info')
      .then(r => setReleaseInfo(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));

    authFetch('/reentry/checklist')
      .then(r => {
        if (r.data?.items?.length > 0) setItems(r.data.items);
      })
      .catch(() => {});
  }, []);

  const toggleItem = async (itemId) => {
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, completed: !i.completed } : i));
    authFetch(`/reentry/checklist/${itemId}/toggle`, { method: 'POST' }).catch(() => {});
  };

  const toggleCat = (cat) => setExpandedCats(p => ({ ...p, [cat]: !p[cat] }));

  const totalItems = items.length;
  const doneItems = items.filter(i => i.completed).length;
  const readinessScore = Math.round((doneItems / totalItems) * 100);
  const profile = user?.profile || {};
  const releaseDate = releaseInfo?.release_date || profile.release_date;
  const daysRemaining = releaseInfo?.days_remaining ?? (releaseDate ? Math.max(0, Math.round((new Date(releaseDate) - new Date()) / 86400000)) : null);

  const grouped = {};
  items.forEach(item => {
    if (!grouped[item.category]) grouped[item.category] = [];
    grouped[item.category].push(item);
  });

  return (
    <div style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="reentry-page">
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>Reentry Planning</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '4px 0 0' }}>Your personalized roadmap for a successful transition.</p>
        </div>

        {/* Top Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {/* Readiness Score */}
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '14px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }} data-testid="readiness-score-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#777777', fontFamily: 'Inter, sans-serif' }}>Readiness Score</span>
              <Compass size={16} color="#F97316" aria-hidden="true" />
            </div>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '48px', color: '#F97316', lineHeight: 1 }}
              aria-label={`Readiness score: ${readinessScore}%`}>{readinessScore}%</div>
            <div className="progress-bar" role="progressbar" aria-valuenow={readinessScore} aria-valuemin={0} aria-valuemax={100} aria-label="Readiness progress">
              <div className="progress-fill" style={{ width: `${readinessScore}%` }} />
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>{doneItems} of {totalItems} items complete</p>
          </div>

          {/* Days Remaining */}
          {daysRemaining !== null && (
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '14px', padding: '20px' }} data-testid="days-remaining-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '13px', color: '#777777' }}>Days to Release</span>
                <Calendar size={16} color="#F97316" aria-hidden="true" />
              </div>
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '48px', color: 'var(--text-primary)', lineHeight: 1 }} aria-label={`${daysRemaining} days to release`}>{daysRemaining}</div>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '8px 0 0' }}>
                {releaseDate ? new Date(releaseDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Release date not set'}
              </p>
            </div>
          )}

          {/* Savings */}
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '14px', padding: '20px' }} data-testid="reentry-savings-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '13px', color: '#777777' }}>Reentry Savings</span>
              <DollarSign size={16} color="#22C55E" aria-hidden="true" />
            </div>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '40px', color: '#22C55E', lineHeight: 1 }}>
              ${(releaseInfo?.reentry_savings ?? profile.reentry_savings ?? 0).toFixed(2)}
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '8px 0 0' }}>From credits converted to cash</p>
          </div>

          {/* Priority Alert */}
          {readinessScore < 50 && (
            <div style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.25)', borderRadius: '14px', padding: '20px', display: 'flex', gap: '12px' }} role="alert">
              <AlertCircle size={20} color="#F97316" style={{ flexShrink: 0, marginTop: '2px' }} aria-hidden="true" />
              <div>
                <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: '#F97316', marginBottom: '6px' }}>Priority Focus</div>
                <p style={{ fontSize: '13px', color: '#888888', margin: 0, lineHeight: 1.6 }}>
                  {daysRemaining && daysRemaining < 180 ? 'Release within 6 months — complete high-priority items now.' : 'Start with Identity Documents — they unlock everything else.'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Tabs: Checklist / Timeline / Resources */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-surface)', borderRadius: '10px', padding: '4px', marginBottom: '20px', width: 'fit-content' }} role="tablist">
          {[{ id: 'checklist', label: 'Checklist' }, { id: 'timeline', label: 'Timeline' }, { id: 'resources', label: 'Resources' }].map(t => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              style={{ padding: '8px 22px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', border: 'none', background: tab === t.id ? 'var(--orange)' : 'transparent', color: tab === t.id ? '#ffffff' : 'var(--text-muted)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              data-testid={`reentry-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Timeline View */}
        {tab === 'timeline' && (
          <div role="tabpanel" aria-label="Timeline view" className="animate-fade-in">
            <ReentryTimeline releaseDate={releaseDate} daysRemaining={daysRemaining} />
          </div>
        )}

        {/* Checklist View */}
        {tab === 'checklist' && (
          <div role="tabpanel" aria-label="Checklist view" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Object.entries(grouped).map(([cat, catItems]) => {
              const meta = CATEGORY_LABELS[cat] || { label: cat, icon: FileText, color: '#F97316' };
              const Icon = meta.icon;
              const catDone = catItems.filter(i => i.completed).length;
              const isExpanded = expandedCats[cat] !== false;

              return (
                <div key={cat} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
                  <button
                    onClick={() => toggleCat(cat)}
                    aria-expanded={isExpanded}
                    aria-controls={`cat-${cat}`}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                    data-testid={`category-${cat}`}
                  >
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(249,115,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={16} color="#F97316" aria-hidden="true" />
                    </div>
                    <span style={{ flex: 1, fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)' }}>{meta.label}</span>
                    <span style={{ fontSize: '12px', color: catDone === catItems.length ? '#22C55E' : '#666666', marginRight: '8px' }}>
                      {catDone}/{catItems.length}
                    </span>
                    {isExpanded ? <ChevronUp size={16} color="#555555" aria-hidden="true" /> : <ChevronDown size={16} color="#555555" aria-hidden="true" />}
                  </button>

                  {isExpanded && (
                    <div id={`cat-${cat}`} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                      {catItems.map((item, idx) => (
                        <div key={item.id} style={{ display: 'flex', gap: '14px', padding: '14px 20px', borderBottom: idx < catItems.length - 1 ? '1px solid var(--border-subtle)' : 'none', cursor: 'pointer' }}
                          role="checkbox"
                          aria-checked={item.completed}
                          tabIndex={0}
                          onClick={() => toggleItem(item.id)}
                          onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggleItem(item.id)}
                          data-testid={`checklist-item-${item.id}`}>
                          <div style={{ flexShrink: 0, marginTop: '2px' }}>
                            {item.completed
                              ? <CheckCircle size={20} color="#22C55E" aria-hidden="true" />
                              : <Circle size={20} color={item.priority === 'high' ? '#F97316' : '#444444'} aria-hidden="true" />
                            }
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: item.completed ? '#555555' : '#F5F5F5', textDecoration: item.completed ? 'line-through' : 'none' }}>
                                {item.title}
                              </span>
                              {item.priority === 'high' && !item.completed && (
                                <span className="badge-orange" style={{ fontSize: '10px', padding: '1px 6px' }}>HIGH</span>
                              )}
                            </div>
                            <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {/* Resources View */}
        {tab === 'resources' && (
          <div role="tabpanel" aria-label="Reentry resources" className="animate-fade-in">
            {/* Category filter */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
              {[{ id: 'all', label: 'All Resources' }, ...Object.entries(RESOURCE_CATEGORIES).map(([id, m]) => ({ id, label: m.label }))].map(c => (
                <button
                  key={c.id}
                  onClick={() => setResourceCat(c.id)}
                  aria-pressed={resourceCat === c.id}
                  data-testid={`resource-filter-${c.id}`}
                  style={{
                    padding: '6px 14px', borderRadius: '20px', border: '1px solid',
                    borderColor: resourceCat === c.id ? 'var(--orange)' : 'var(--border-default)',
                    background: resourceCat === c.id ? 'rgba(249,115,22,0.1)' : 'transparent',
                    color: resourceCat === c.id ? 'var(--orange)' : 'var(--text-secondary)',
                    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                  }}>
                  {c.label}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {REENTRY_RESOURCES
                .filter(r => resourceCat === 'all' || r.category === resourceCat)
                .map(org => {
                  const meta = RESOURCE_CATEGORIES[org.category];
                  const Icon = meta?.icon || FileText;
                  const color = meta?.color || '#F97316';
                  return (
                    <div
                      key={org.id}
                      data-testid={`resource-card-${org.id}`}
                      style={{
                        background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                        borderRadius: '14px', padding: '18px 20px',
                        display: 'flex', gap: '14px', alignItems: 'flex-start',
                      }}>
                      <div style={{ width: 36, height: 36, borderRadius: '10px', background: `${color}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={16} style={{ color }} aria-hidden="true" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                          <div>
                            <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)', margin: '0 0 2px' }}>
                              {org.name}
                            </h3>
                            <span style={{ fontSize: '11px', fontWeight: 600, color, padding: '2px 7px', borderRadius: '10px', background: `${color}18` }}>
                              {meta?.label}
                            </span>
                            {org.national && (
                              <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '6px' }}>• Nationwide</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', flexShrink: 0, flexWrap: 'wrap' }}>
                            {org.phone && (
                              <a href={`tel:${org.phone.replace(/[^0-9]/g, '')}`}
                                data-testid={`resource-phone-${org.id}`}
                                aria-label={`Call ${org.name}: ${org.phone}`}
                                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '7px', border: '1px solid var(--border-default)', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, textDecoration: 'none', fontFamily: 'Inter, sans-serif', background: 'var(--bg-elevated)' }}>
                                <Phone size={11} aria-hidden="true" />{org.phone}
                              </a>
                            )}
                            {org.website && (
                              <a href={org.website} target="_blank" rel="noopener noreferrer"
                                data-testid={`resource-website-${org.id}`}
                                aria-label={`Visit ${org.name} website`}
                                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '7px', background: 'var(--orange)', color: '#ffffff', fontSize: '12px', fontWeight: 700, textDecoration: 'none', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                                <Globe size={11} aria-hidden="true" />Visit
                              </a>
                            )}
                          </div>
                        </div>
                        <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '8px 0 0', lineHeight: 1.6 }}>{org.desc}</p>
                      </div>
                    </div>
                  );
                })}
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', marginTop: '24px', lineHeight: 1.6 }}>
              Ask your case manager to help connect you to local chapters of these organizations.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
